import { createSelector } from '@ngrx/store';
import {
  recentPurchaseAdapter,
  selectRecentPurchaseState,
  selectRecentPurchaseSearchSortFilterOptions,
  ListConstants,
  ListSearchSortFilterOptions,
  IRecentPurchaseState,
  SelectedListProductNumbers,
  selectSelectedListProductNumbers,
} from '@usf/ngrx-list';
import {
  orderHeaderSelectors,
  OrderHeaderState,
  orderItemSelectors,
  OrderItemState,
} from '@usf/ngrx-order';
import { Product } from 'src/app/shared/models/product.model';
import { productWithAlternativesSelector } from 'src/app/shared/selectors/product.selectors';
import {
  addProductClass,
  compareProductDesc,
  comparePurchaseDates,
  createRecentPurchaseRow,
  sortByProductClasses,
} from './helpers/recent-purchase.selectors.helper';
import { RecentlyPurchasedViewModel } from '../model/recently-purchased-view.model';
import {
  calculateDesktopItemHeightsAndIds,
  calculateMobileItemHeights,
  calculateTabletItemHeightsAndIds,
  isInFilter,
  productMatchesKeyword,
  getSortAndFilterCount,
  getListSearchSortFilterOptions,
} from '../../../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { PlatformEnum } from '../../../../shared/constants/platform-type.enum';
import {
  ItemTypes,
  ListRow,
} from '../../../shared/list-detail-management/model/list-detail-management-view.model';
import { getCustUserProfiles } from '../../../../home/selectors/home.selectors';
import { SelectedCustUserProfiles } from '../../../../home/models/user-customer-info.model';
import {
  IInventoryUIConstantsState,
  selectInventoryUIConstantsState,
} from '@usf/ngrx-inventory';
import { CreateInventoryModal } from '@inventory/constants/inventory-constants';
import { ListTypes } from '../../../../shared/constants/lists-constants';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  PlatformState,
  selectAllFeaturesEnabled,
  selectPlatform,
} from '@panamax/app-state';
import { selectUserPreferences } from '@app/user/store';
import { Preferences } from '@usf/user-types/user-preference';
import * as dayjs from 'dayjs';

export const selectRecentlyPurchasedProductsLoaded = () =>
  createSelector(
    selectRecentPurchaseState,
    recentProducts => recentProducts.loaded,
  );

export const selectRecentlyPurchasedProductNumbers = () =>
  createSelector(
    recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds,
    (productNumbers: number[]) => productNumbers,
  );

export const selectIfDaysAreWithinRecentPuchaseRange = () =>
  createSelector(
    recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds,
    productWithAlternativesSelector,
    selectInventoryUIConstantsState,
    (
      productNumbers: number[],
      summaryState,
      constants: IInventoryUIConstantsState,
    ) => {
      // 200 is the code used for the second set of categories to access past purchase days.
      let subCategory =
        constants.inventoryUIConstants.createInventoryInfo.categories
          .find(value => value.code === '200')
          .subCategories.find(
            value => value.code === CreateInventoryModal.pastPurchase.valueOf(),
          );
      let timeFrame = subCategory.subCategoryDetails.map(
        value => value.pastPurchaseDays,
      );

      let gatesPassed: boolean[] = [];
      // Creating the date checks that links to the filters
      let dateValuesFromApi = timeFrame.map(daysToGoBack => {
        gatesPassed.push(false);
        let dateValue = new Date();
        dateValue.setDate(dateValue.getDate() - daysToGoBack);
        return dateValue;
      });

      const datePassesGate = (dateCheck: Date, dateGate: Date) => {
        return (
          dateCheck.getFullYear() > dateGate.getFullYear() ||
          (dateCheck.getFullYear() === dateGate.getFullYear() &&
            dateCheck.getMonth() > dateGate.getMonth()) ||
          (dateCheck.getMonth() === dateGate.getMonth() &&
            dateCheck.getDate() >= dateGate.getDate())
        );
      };

      // Go through all of the products that are part of recent purchase
      // If the purchaseDate is less than a given check then turn on flag
      // Skip checks if they are already true
      summaryState.forEach(productSummary => {
        if (
          productNumbers.includes(productSummary.productNumber) &&
          productSummary.recentPurchase
        ) {
          let dateFromRecentPurchaseList = new Date(
            productSummary.recentPurchase.purchaseDtm1,
          );

          dateValuesFromApi.forEach((dateGate, index) => {
            if (
              !gatesPassed[index] &&
              datePassesGate(dateFromRecentPurchaseList, dateGate)
            ) {
              gatesPassed[index] = true;
            }
          });
        }
      });

      return gatesPassed;
    },
  );

export const isRPAndProductDataLoaded = () =>
  createSelector(
    recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds,
    productWithAlternativesSelector,
    selectRecentlyPurchasedProductsLoaded(),
    (productNumbers, summaryState, rpDataIsLoaded) => {
      let isLoaded = true;

      if (!rpDataIsLoaded) {
        isLoaded = false;
      }

      productNumbers.forEach(prodNum => {
        if (!summaryState.get(prodNum)?.alternative) {
          isLoaded = false;
        }
      });
      return isLoaded;
    },
  );

export const isRPAndProductAndPricingDataLoaded = () =>
  createSelector(
    recentPurchaseAdapter.getSelectors(selectRecentPurchaseState).selectIds,
    productWithAlternativesSelector,
    selectRecentlyPurchasedProductsLoaded(),
    (productNumbers, summaryState, rpDataIsLoaded) => {
      let isLoaded = true;

      if (!rpDataIsLoaded) {
        isLoaded = false;
      }
      productNumbers.forEach(prodNum => {
        const product = summaryState.get(prodNum);
        if (
          !product ||
          product?.pricing?.loading ||
          !product?.pricing?.productNumber
        ) {
          isLoaded = false;
        }
      });
      return {
        isLoaded,
        productNumbers: productNumbers.map(number =>
          new Number(number).valueOf(),
        ),
      };
    },
  );

export const selectRecentPurchaseStickySortFilter = createSelector(
  selectRecentPurchaseSearchSortFilterOptions,
  selectUserPreferences,
  (
    recentPurchaseSearchSortFilterOptions: ListSearchSortFilterOptions,
    userPreferences: Preferences,
  ) => {
    let preferencesForThisList;

    if (userPreferences?.preferences?.sortAndFilterApplied) {
      preferencesForThisList =
        userPreferences.preferences.sortAndFilterApplied[
          ListConstants.recentlyPurchased
        ];
    }

    return getListSearchSortFilterOptions(
      recentPurchaseSearchSortFilterOptions,
      preferencesForThisList,
    );
  },
);

export const selectRpSelections = () =>
  createSelector(
    selectRecentPurchaseState,
    productWithAlternativesSelector,
    (
      recentPurchaseState: IRecentPurchaseState,
      productMap: Map<number, Product>,
    ): Product[] => {
      const products: Product[] = [];
      recentPurchaseState.ids.forEach(id => {
        const recentPurchase = recentPurchaseState.entities[id];
        if (
          recentPurchaseState.selections[recentPurchase.productNumber] &&
          productMap.has(recentPurchase.productNumber)
        ) {
          const productFound = productMap.get(recentPurchase.productNumber);
          products.push(productFound);
        }
      });
      return products;
    },
  );

export const filterByWeeks = (
  recentPurchaseState: IRecentPurchaseState,
  is52weeks: boolean,
): number[] => {
  // For 52 weeks, return all numeric IDs
  if (is52weeks) {
    return recentPurchaseState.ids
      .map(id => (typeof id === 'number' ? id : null))
      .filter((id): id is number => id !== null);
  }

  const cutoffDate = dayjs().subtract(26, 'weeks');

  // For 26 weeks, filter by date
  return recentPurchaseState.ids
    .map(id => {
      if (typeof id !== 'number') return null;
      const purchase = recentPurchaseState.entities[id];
      return purchase && dayjs(purchase.purchaseDtm1).isAfter(cutoffDate)
        ? id
        : null;
    })
    .filter((id): id is number => id !== null);
};

export const selectRpVm = (
  productNumbers: number[],
  platformType?: PlatformEnum,
) =>
  createSelector(
    getCustUserProfiles,
    productWithAlternativesSelector,
    orderItemSelectors.selectOrderItemState,
    orderHeaderSelectors.selectOrderContextState,
    selectRecentPurchaseStickySortFilter,
    selectAllFeaturesEnabled([FEATURES.split_global_download_print.name]),
    selectRecentPurchaseState,
    selectSelectedListProductNumbers,
    selectPlatform(),
    (
      selectedCustProfiles: SelectedCustUserProfiles,
      products: Map<number, Product>,
      orderItemState: OrderItemState,
      orderHeaderState: OrderHeaderState,
      recentPurchaseSearchSortFilterOptions: ListSearchSortFilterOptions,
      globalDownladFlag: boolean,
      recentPurchaseState: IRecentPurchaseState,
      selectedListViewModel: SelectedListProductNumbers,
      platform: PlatformState,
    ): RecentlyPurchasedViewModel => {
      let totalItemCount = recentPurchaseState.ids.length;
      let productClasses: string[] = [];
      let items: ListRow[] = [];
      let productsAfterFilter = 0;
      let numProductsSelected = 0;
      let unselectedItemFound = false;

      const defaultSort =
        recentPurchaseSearchSortFilterOptions.sortType == ListConstants.date;
      const alphaSort =
        recentPurchaseSearchSortFilterOptions.sortType ==
        ListConstants.alphabetical;
      const classSort =
        recentPurchaseSearchSortFilterOptions.sortType ===
        ListConstants.usFoodsClass;

      Object.keys(recentPurchaseState.selections).forEach(key => {
        if (!isNaN(Number(key))) {
          if (recentPurchaseState.selections[key] === true) {
            numProductsSelected++;
          }
        }
      });

      const filteredIds = filterByWeeks(
        recentPurchaseState,
        recentPurchaseSearchSortFilterOptions?.week52Filter,
      );

      filteredIds.forEach(id => {
        const product = products.get(id);
        const item = createRecentPurchaseRow(id, products);
        productClasses = addProductClass(item.summary, productClasses);
        if (
          productMatchesKeyword(
            recentPurchaseSearchSortFilterOptions.searchKey,
            item?.summary,
            item?.alternative,
            item?.customerProductNumber,
          ) &&
          isInFilter(
            item?.summary,
            recentPurchaseSearchSortFilterOptions.filters,
          )
        ) {
          const recentPurchase = recentPurchaseState.entities[id];
          item.selected =
            recentPurchaseState.selections[recentPurchase.productNumber] ??
            false;
          productsAfterFilter++;
          if (!item.selected) {
            unselectedItemFound = true;
          }
          if (defaultSort) {
            items.push({ ...item, groupName: 'Miscellaneous' });
          } else if (alphaSort) {
            items.push({ ...item, groupName: 'Alphabetical' });
          } else {
            items.push(item);
          }
        }
      });

      if (defaultSort) {
        items.sort(comparePurchaseDates);
        if (items.length > 0) {
          items.unshift({
            groupName: 'Miscellaneous',
            itemCount: items.length,
            itemType: ItemTypes.group,
          });
        }
      } else if (alphaSort) {
        items.sort(compareProductDesc);
        if (items.length > 0) {
          items.unshift({
            groupName: 'Alphabetical',
            itemCount: items.length,
            itemType: ItemTypes.group,
          });
        }
      } else if (classSort) {
        items.sort(compareProductDesc);
        items = sortByProductClasses(
          items,
          productClasses,
          recentPurchaseSearchSortFilterOptions.groupToSort,
        );
      }

      let newItemHeights;
      let productCardIds;
      let showEllipsis = globalDownladFlag;

      if (platformType === PlatformEnum.desktop) {
        const ret = calculateDesktopItemHeightsAndIds(
          items,
          ListTypes.recentlyPurchased,
          selectedListViewModel.selectedListViewMode,
          platform.width,
        );
        newItemHeights = ret.itemHeights;
        productCardIds = ret.productCardIds;
        showEllipsis = false;
      } else if (platformType === PlatformEnum.tablet) {
        showEllipsis = false;
        const ret = calculateTabletItemHeightsAndIds(
          items,
          selectedListViewModel.selectedListViewMode,
        );
        newItemHeights = ret.itemHeights;
        productCardIds = ret.productCardIds;
      } else if (platformType === PlatformEnum.mobile) {
        newItemHeights = calculateMobileItemHeights(
          items,
          ListTypes.recentlyPurchased,
          selectedListViewModel.selectedListViewMode,
        );
      }

      return {
        selectedCustomer: selectedCustProfiles.selectedCustomer,
        items,
        productClasses,
        totalItemCount,
        orderItemTallies: orderItemState?.tallies,
        orderHeader: orderHeaderState?.entities[orderHeaderState?.ids[0]],
        itemHeights: newItemHeights,
        showEllipsis,
        productCardIds: productCardIds,
        sortAndFilterCount: getSortAndFilterCount(
          recentPurchaseSearchSortFilterOptions,
          ListConstants.date,
        ),
        searchSortFilterOptionsInRecentPurchase:
          recentPurchaseSearchSortFilterOptions,
        isSelected: recentPurchaseState.isSelected ?? false,
        numProductsSelected,
        productsAfterFilter,
        showSelectAll: unselectedItemFound,
        selectedListViewModel,
        platform,
      } as RecentlyPurchasedViewModel;
    },
  );
